// RebuildElevationButton — manueller "Höhenprofil neu bauen"-Knopf für Trails.
//
// User-Wunsch (Wave 14ar):
//   "ok, besser mit einem button wenn ich einen trail öffne 'rebuild elevation'.
//    sollte im on device admin mode sichtbar sein. dann wird auf wunsch die
//    höhenlinie für den trail neu gebaut, weil ich sehe es immernoch flach,
//    leider. wenn du ERKENNST dass sie flach ist, dann gern auch ohne on
//    device admin mode."
//
// Sichtbarkeit (zwei Wege):
//   1. Admin-Mode ON → Button IMMER sichtbar (wie AddCruxHereButton). Gibt dem
//      Admin die explizite Kontrolle, egal ob das Profil aktuell gut aussieht.
//   2. Admin-Mode OFF → Button NUR sichtbar wenn wir einen "flat-line"-Verdacht
//      haben (source='srtm'|'gpx' aber Min/Max-Spread <5m über Tracks >200m).
//      Ein 2km-Trail mit 1m Gesamthub ist technisch möglich aber extrem selten
//      in Liguria/Finale; meistens ist's ein SRTM-Glitch mit nur einem validen
//      DEM-Tile während des Samplings.
//
// Click-Flow:
//   1. elevation.rebuild() → löscht IDB-Cache für Trail, setzt source='pending',
//      startet den Retry-Loop komplett neu (max ~6s bei 3 Retries).
//   2. UI wechselt während pending auf "Rebuilding…" (disabled).
//   3. Nach settled: kurzer Flash (grün bei Erfolg, rot bei 'none'), dann
//      zurück auf Default-Label. Pending-Timing ist nicht unser Job — der
//      `source`-Wert im data-Objekt reflektiert den echten Hook-State.

import { useEffect, useRef, useState } from 'react';
import type { TrailElevationData } from '../lib/useTrailElevation';
import { useAdminMode } from '../lib/useUiSettings';

export interface RebuildElevationButtonProps {
  data: TrailElevationData;
}

/**
 * Schwellwert: wenn der aktuelle Trail länger als X Meter ist UND der Höhen-
 * Spread kleiner als Y Meter, gilt das Profil als "verdächtig flach".
 *
 * 200m Mindest-Länge: bei sehr kurzen Connectoren (50-100m) ist echte 1m-
 * Flachheit plausibel. Ab 200m+ sollte jeder MTB-Trail in Finale mehr Hub
 * haben als 5m.
 *
 * 5m Spread: der strikte Check in buildSrtmElevationSamples lässt ab 2m
 * Spread durch — aber 2-5m ist immer noch suspekt (ein einziges DEM-Tile mit
 * Noise kann das erzeugen). 5m ist eine humane Grenze, wo der Button
 * auftaucht um dem User die Option zu geben.
 */
const FLAT_SUSPICION_MIN_TRACK_METERS = 200;
const FLAT_SUSPICION_MAX_SPREAD_METERS = 5;

function looksFlat(data: TrailElevationData): boolean {
  if (!data.elevations || data.elevations.length < 2) return false;
  if (data.totalMeters < FLAT_SUSPICION_MIN_TRACK_METERS) return false;
  // 'pending' und 'none' nicht als "flach" werten — für die existieren andere
  // UI-Zustände (Spinner bzw. "No data"-Label).
  if (data.source === 'pending' || data.source === 'none') return false;

  let min = Infinity;
  let max = -Infinity;
  for (const v of data.elevations) {
    if (v < min) min = v;
    if (v > max) max = v;
  }
  return max - min < FLAT_SUSPICION_MAX_SPREAD_METERS;
}

export function RebuildElevationButton({ data }: RebuildElevationButtonProps) {
  const [adminMode] = useAdminMode();
  const [flash, setFlash] = useState<'success' | 'error' | null>(null);
  // Tracken ob wir AKTUELL in einer User-initiierten Rebuild-Action sind.
  // Wir können das nicht allein am `source==='pending'` festmachen, weil der
  // Initial-Load auch pending ist — dann wollen wir aber keinen "Rebuilding…"-
  // Button zeigen.
  const [busy, setBusy] = useState(false);
  // Nach einem Rebuild-Click wollen wir erkennen wann der Hook "fertig" ist,
  // um die success/error-Flash zu zeigen. Wir markieren das durch einen Wert
  // in busyRef, und checken bei jedem data-Update ob wir raus aus 'pending' sind.
  const busyRef = useRef(false);

  const isFlat = looksFlat(data);
  // Nicht rendern wenn's nix zu rebuilden gibt: Profil hat eh keine Samples
  // (source='none' → Button bringt auch nix, der Retry-Loop lief durch) oder
  // Trail noch im Initial-Load (pending ohne User-Click) im Non-Admin-Case.
  const shouldShow = (adminMode || isFlat) && data.elevations != null;

  // Beim Rebuild warten wir auf den Source-Wechsel (pending → srtm/gpx/none),
  // um Flash zu setzen. Effect feuert bei jeder data.source-Änderung.
  useEffect(() => {
    if (!busyRef.current) return;
    if (data.source === 'pending') return; // warte bis fertig
    busyRef.current = false;
    setBusy(false);
    // Erfolg = wir haben Samples und sie sehen nicht mehr flach aus.
    // Misserfolg = noch immer flach oder source='none'.
    if (data.source === 'none' || looksFlat(data)) {
      setFlash('error');
    } else {
      setFlash('success');
    }
    const t = window.setTimeout(() => setFlash(null), 2000);
    return () => window.clearTimeout(t);
  }, [data.source, data.elevations]);

  if (!shouldShow) return null;

  const onClick = async () => {
    if (busy) return;
    setBusy(true);
    busyRef.current = true;
    setFlash(null);
    try {
      await data.rebuild();
    } catch (err) {
      // rebuild() fängt intern, aber defensiv trotzdem.
      console.warn('[rebuild-elev] rebuild threw', err);
      busyRef.current = false;
      setBusy(false);
      setFlash('error');
      window.setTimeout(() => setFlash(null), 2000);
    }
  };

  // Labels: kurz, weil der Button in einer Reihe mit anderen kompakten
  // Buttons (Ride, Navigate, CruxToggle …) sitzt.
  const label = busy
    ? 'Rebuilding…'
    : flash === 'success'
    ? 'Rebuilt ✓'
    : flash === 'error'
    ? 'No data'
    : 'Rebuild elev.';

  const title = busy
    ? 'Fetching fresh SRTM samples (up to 6 seconds)…'
    : flash === 'error'
    ? 'SRTM still returned no elevation data — try zooming to the trail on the map first'
    : isFlat
    ? 'Current profile looks flat — tap to re-sample SRTM elevation data'
    : 'Re-sample SRTM elevation data for this trail';

  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      disabled={busy}
      className={`flex-shrink-0 transition-colors px-2.5 py-1.5 rounded-lg flex items-center justify-center gap-1.5 border font-medium text-[12px] leading-tight ${
        flash === 'success'
          ? 'bg-emerald-500/90 text-white border-emerald-400'
          : flash === 'error'
          ? 'bg-red-600/90 text-white border-red-400'
          : busy
          ? 'bg-turbo-700/30 text-white/60 border-white/10 cursor-wait'
          : 'bg-sky-500/20 hover:bg-sky-500/30 active:bg-sky-500/40 text-sky-200 border-sky-400/40'
      }`}
    >
      <MountainProfileIcon />
      <span className="text-center whitespace-nowrap">{label}</span>
    </button>
  );
}

/** Kleines Berg-Profil-Icon — passt thematisch zum Elevation-Widget. */
function MountainProfileIcon() {
  return (
    <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true" style={{ display: 'block' }}>
      <path
        d="M2 15 L7 8 L10 11 L14 5 L18 15 Z"
        fill="currentColor"
        opacity="0.9"
      />
    </svg>
  );
}
